import * as BackgroundTask from 'expo-background-task';
import * as TaskManager from 'expo-task-manager';
import React, { useEffect, useMemo } from 'react';
import { AppStack } from './app-stack';
import { AuthStatus } from '@/enums';
import { CONTAINER_MODAL_ROUTES } from './routes';
import { ContainerModalStackParams } from './types';
import { CountrySelectorScreen } from '@/screens/country-selector-screen';
import { createStackNavigator } from '@react-navigation/stack';
import { DemoSettingsScreen } from '@/screens/demo-settings-screen';
import {
  flexModalCardStyle,
  useModalStackScreenOptions,
} from '@/hooks/use-modal-stack-screen-options';
import { InboundOfflineScreen } from '@/screens/order-inbounds/inbound-offline-screen';
import { LicensesScreen } from '@/screens/licenses-screen';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import {
  NavigationGuardStatus,
  NavigationStackGuard,
  UserContextSyncGuard,
} from '@/components/navigation-stack-guard';
import {
  NetworkStatus,
  useNetworkStatus,
} from '@/contexts/network-status-context';
import { OrderInboundsStack } from './order-inbounds-stack';
import { partnerSupplier } from '@/utils/environment';
import { PlacesSearchScreen } from '@/screens/places-search-screen';
import { Platform } from 'react-native';
import { SubscriptionContextProvider } from '@/contexts/subscription-context';
import { SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID } from '@/background-tasks/offline-data-sync';
import { useAppMinimumVersion } from '@/hooks/use-app-minimum-version';
import { useAuth } from '@/contexts/auth-context';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useMaybeUserContext } from '@/contexts/user-context';
import { useNavigationGuardStatus } from '@/hooks/use-navigation-guard-status';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { usePrevious } from '@/hooks/use-previous';

const ModalStack = createStackNavigator<ContainerModalStackParams>();

export const ContainerStack: React.FC = () => {
  const modalScreenOptions = useModalStackScreenOptions();
  const defaultScreenOptions = useDefaultStackScreenOptions();
  const { appMinimumVersion } = useAppMinimumVersion();
  const { isOrderAgentOnly } = useOrderInbounds();

  const { networkStatus } = useNetworkStatus();
  const previousNetworkStatus = usePrevious<NetworkStatus>(networkStatus);

  // Read auth status from AuthContext (not UserContext)
  const authStatus = useAuth((s) => s.authStatus);
  const checkAuthStatus = useAuth((s) => s.checkAuthStatus);

  // UserContext is still needed for refetching restaurant-scoped data
  const context = useMaybeUserContext();
  const refetchUserContext = context?.refetch;

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const getBackgroundTaskStatus = async () => {
      const isTaskDefined = TaskManager.isTaskDefined(
        SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID,
      );
      const isRegistered = await TaskManager.isTaskRegisteredAsync(
        SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID,
      );
      const isAvailable = await TaskManager.isAvailableAsync();
      return { canRegister: isTaskDefined && isAvailable, isRegistered };
    };

    const registerBackgroundSync = async () => {
      const { canRegister, isRegistered } = await getBackgroundTaskStatus();
      if (canRegister && !isRegistered) {
        await BackgroundTask.registerTaskAsync(
          SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID,
        );
      }
    };

    const unregisterBackgroundSync = async () => {
      const { isRegistered } = await getBackgroundTaskStatus();
      if (isRegistered) {
        try {
          await BackgroundTask.unregisterTaskAsync(
            SYNC_OFFLINE_DATA_BACKGROUND_TASK_ID,
          );
        } catch {
          // Task may not be registered with BackgroundTask even if TaskManager
          // reports it as registered (e.g., task was defined but never scheduled).
          // This is expected and safe to ignore.
        }
      }
    };

    if (context?.authStatus === AuthStatus.Authenticated) {
      registerBackgroundSync();
    } else {
      unregisterBackgroundSync();
    }
  }, [context?.authStatus]);

  // Note: context and refetchUserContext are used in the useEffect below
  // for handling network reconnection

  useEffect(() => {
    // Force a refresh of the user's auth when going from offline to online
    if (
      (authStatus === AuthStatus.NotAuthenticated ||
        authStatus === AuthStatus.Offline) &&
      networkStatus === NetworkStatus.CONNECTED &&
      previousNetworkStatus === NetworkStatus.DISCONNECTED
    ) {
      refetchUserContext?.();
      checkAuthStatus?.({ reset: true });
    }
  }, [
    authStatus,
    checkAuthStatus,
    networkStatus,
    previousNetworkStatus,
    refetchUserContext,
  ]);

  const screenOptions = useMemo(() => {
    return {
      ...defaultScreenOptions,
      cardOverlayEnabled: false,
    };
  }, [defaultScreenOptions]);

  // Calculate navigation guard status based on auth, network, and version requirements
  // This hook reads directly from AuthContext, making the logic reusable across
  // different experiences (operator portal, sales portal, etc.)
  const navigationGuardStatus = useNavigationGuardStatus();

  // Wait for navigation guard status and user permissions to be determined
  if (!navigationGuardStatus || isOrderAgentOnly === null) {
    return <LogoLoadingIndicator />;
  }

  // UserContext synchronization is now handled by UserContextSyncGuard inside
  // NavigationStackGuard. This provides defense-in-depth by checking synchronization
  // at the final moment before rendering AppStack, catching any race conditions that
  // occur during the rendering cycle.

  return (
    <SubscriptionContextProvider>
      <ModalStack.Navigator
        initialRouteName={
          isOrderAgentOnly
            ? CONTAINER_MODAL_ROUTES.OrderInbounds
            : CONTAINER_MODAL_ROUTES.Base
        }
        screenOptions={modalScreenOptions}
      >
        <ModalStack.Screen
          name={CONTAINER_MODAL_ROUTES.Base}
          options={screenOptions}
        >
          {() => (
            <NavigationStackGuard
              appMinimumVersion={appMinimumVersion}
              navigationGuardStatus={navigationGuardStatus}
            >
              <UserContextSyncGuard
                navigationGuardStatus={navigationGuardStatus}
              >
                <AppStack />
              </UserContextSyncGuard>
            </NavigationStackGuard>
          )}
        </ModalStack.Screen>
        <ModalStack.Screen
          component={CountrySelectorScreen}
          name={CONTAINER_MODAL_ROUTES.CountrySelector}
          options={{ cardStyle: flexModalCardStyle }}
        />
        <ModalStack.Screen
          name={CONTAINER_MODAL_ROUTES.OrderInbounds}
          options={screenOptions}
        >
          {() => (
            <NavigationStackGuard
              allowedStatuses={[NavigationGuardStatus.AUTHENTICATED]}
              appMinimumVersion={appMinimumVersion}
              fallbackComponents={{
                [NavigationGuardStatus.OFFLINE_MODE]: InboundOfflineScreen,
                [NavigationGuardStatus.OFFLINE_SCREEN]: InboundOfflineScreen,
                [NavigationGuardStatus.NOT_AUTHENTICATED]: InboundOfflineScreen,
              }}
              navigationGuardStatus={navigationGuardStatus}
            >
              <UserContextSyncGuard
                navigationGuardStatus={navigationGuardStatus}
              >
                <OrderInboundsStack />
              </UserContextSyncGuard>
            </NavigationStackGuard>
          )}
        </ModalStack.Screen>
        {partnerSupplier.demoEnabled ? (
          <ModalStack.Screen
            component={DemoSettingsScreen}
            name={CONTAINER_MODAL_ROUTES.DemoSettings}
          />
        ) : null}
        <ModalStack.Screen
          component={PlacesSearchScreen}
          name={CONTAINER_MODAL_ROUTES.PlacesSearch}
          options={{ cardStyle: flexModalCardStyle }}
        />
        <ModalStack.Screen
          component={LicensesScreen}
          name={CONTAINER_MODAL_ROUTES.Licenses}
          options={{ cardStyle: flexModalCardStyle }}
        />
      </ModalStack.Navigator>
    </SubscriptionContextProvider>
  );
};
